<template>
  <v-card flat class="vetalfa-view white">
    <div ref="screenHeader" class="vetalfa-global-header">
      <vetalfa-small-header isAppHeader></vetalfa-small-header>
      <v-divider></v-divider>
    </div>
    <div ref="screenContent">
      <!-- Mobile: uma coluna, hierarquia por espaço e fundos suaves. -->
      <main v-if="mobile" class="va-home va-home-mobile">
        <vetalfa-home-search-card
          mobile
          @select="onSearch"
        ></vetalfa-home-search-card>

        <vetalfa-home-continue-card
          v-if="continueItem"
          :item="continueItem"
          @select="onContinue"
        ></vetalfa-home-continue-card>

        <vetalfa-home-quick-access
          :sections="mobileQuickAccessSections"
          :limit="6"
          mobile
          :is-premium="isPremium"
          @select="onQuickAccessSelect"
          @open-all-sections="onOpenAllSections"
        ></vetalfa-home-quick-access>

        <vetalfa-home-recommended
          v-if="showRecommended"
          :items="recommendations"
          mobile
          @select="onRecommendedSelect"
          @shuffle="onRecommendedShuffle"
        ></vetalfa-home-recommended>

        <vetalfa-home-upsell-card
          v-if="!isPremium"
          @select="onUpsellSelect"
        ></vetalfa-home-upsell-card>

        <div v-if="hasActions" class="va-home-actions">
          <vetalfa-home-action-row
            v-if="toggles['ai_search']"
            :mdi="mdiCreation"
            text="Pergunte ao assistente de medicina veterinária"
            @select="onAssistant"
          ></vetalfa-home-action-row>
          <vetalfa-home-action-row
            v-if="toggles['prescriptions']"
            :mdi="mdiPrescription"
            text="Prescreva medicamentos com facilidade para os seus pacientes."
            @select="onPrescriptions"
          ></vetalfa-home-action-row>
        </div>

        <vetalfa-home-favorites
          v-if="showFavorites"
          :items="favoriteItems"
          mobile
          @select="onFavoriteSelect"
          @view-all="onFavoritesViewAll"
        ></vetalfa-home-favorites>
      </main>

      <!-- Desktop: conteúdo à esquerda, trilho à direita. -->
      <main v-else class="va-home va-home-desktop">
        <div class="va-home-main">
          <vetalfa-home-search-card
            @select="onSearch"
          ></vetalfa-home-search-card>

          <!-- No desktop cabe a lista inteira, então o botão do painel sai. -->
          <vetalfa-home-quick-access
            :sections="allSections"
            :is-premium="isPremium"
            :show-all-action="false"
            @select="onQuickAccessSelect"
          ></vetalfa-home-quick-access>

          <vetalfa-home-recommended
            v-if="showRecommended"
            :items="recommendations"
            @select="onRecommendedSelect"
            @shuffle="onRecommendedShuffle"
          ></vetalfa-home-recommended>

          <vetalfa-home-favorites
            v-if="showFavorites"
            :items="favoriteItems"
            @select="onFavoriteSelect"
            @view-all="onFavoritesViewAll"
          ></vetalfa-home-favorites>
        </div>

        <aside class="va-home-rail">
          <div v-if="continueItem || hasActions" class="va-home-rail-panel">
            <vetalfa-home-continue-card
              v-if="continueItem"
              :item="continueItem"
              row
              @select="onContinue"
            ></vetalfa-home-continue-card>
            <vetalfa-home-action-row
              v-if="toggles['ai_search']"
              :mdi="mdiCreation"
              text="Pergunte ao assistente de medicina veterinária"
              row
              @select="onAssistant"
            ></vetalfa-home-action-row>
            <vetalfa-home-action-row
              v-if="toggles['prescriptions']"
              :mdi="mdiPrescription"
              text="Prescreva medicamentos com facilidade para os seus pacientes."
              row
              @select="onPrescriptions"
            ></vetalfa-home-action-row>
          </div>

          <vetalfa-home-upsell-card
            v-if="!isPremium"
            @select="onUpsellSelect"
          ></vetalfa-home-upsell-card>
        </aside>
      </main>
    </div>
  </v-card>
</template>

<style>
.va-home {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
}

.va-home-mobile {
  display: flex;
  flex-direction: column;
  gap: 22px;
  /* O topo ja recebe 12px de _setupScreenContent abaixo do header fixo. */
  padding: 4px 16px 110px;
}

.va-home-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.va-home-desktop {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 36px;
  padding: 28px 16px 110px;
}

.va-home-main {
  display: flex;
  flex-direction: column;
  gap: 26px;
  min-width: 0;
}

.va-home-rail {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
}

.va-home-rail-panel {
  display: flex;
  flex-direction: column;
  padding: 4px 16px;
  border-radius: 14px;
  background: #f5f6fa;
}
</style>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import { mdiCreation, mdiPrescription } from "@mdi/js";
import sectionsEnum from "@/constants/sections.enum.js";
import contentRoutes from "@/constants/content.routes.js";
import HomeSearchCard from "@/components/home/HomeSearchCard.vue";
import HomeActionRow from "@/components/home/HomeActionRow.vue";
import HomeContinueCard from "@/components/home/HomeContinueCard.vue";
import HomeFavorites from "@/components/home/HomeFavorites.vue";
import HomeRecommended from "@/components/home/HomeRecommended.vue";
import HomeQuickAccess from "@/components/home/HomeQuickAccess.vue";
import HomeUpsellCard from "@/components/home/HomeUpsellCard.vue";

const UPSELL_IMPRESSION_KEY = "vetalfa-home-upsell-impression";
const SHUFFLE_DEBOUNCE = 300;

export default {
  name: "Home",
  components: {
    "vetalfa-home-search-card": HomeSearchCard,
    "vetalfa-home-action-row": HomeActionRow,
    "vetalfa-home-continue-card": HomeContinueCard,
    "vetalfa-home-favorites": HomeFavorites,
    "vetalfa-home-recommended": HomeRecommended,
    "vetalfa-home-quick-access": HomeQuickAccess,
    "vetalfa-home-upsell-card": HomeUpsellCard
  },
  computed: {
    ...mapGetters("general", ["user", "isPremium", "toggles"]),
    ...mapGetters("preferences", [
      "favorites",
      "lastSeenItem",
      "homeRecommendations",
      "triggerFavoritesChange"
    ]),
    mobile() {
      return this._isMobile();
    },
    hasActions() {
      return !!(
        this.toggles &&
        (this.toggles["ai_search"] || this.toggles["prescriptions"])
      );
    },
    /** Mobile: banner de Medicamentos e a linha de circulos, na ordem do catalogo. */
    mobileQuickAccessSections() {
      return sectionsEnum.getMobileHomeSections(this.toggles);
    },
    /** Desktop: todas as seções de conteúdo e suporte clínico. */
    allSections() {
      return sectionsEnum.getAllSections(this.toggles);
    },
    /** Favoritos com rota resolvível. */
    favoriteItems() {
      return (this.favorites || []).filter(f =>
        contentRoutes.resolveContentPath(f)
      );
    },
    showFavorites() {
      return this.favoriteItems.length > 0;
    },
    hasFavorites() {
      return this.favoriteItems.length > 0;
    },
    hasHistory() {
      return !!this.lastSeenItem;
    },
    /** O último conteúdo aberto neste dispositivo, sempre que houver um. */
    continueItem() {
      return this.lastSeenItem || null;
    },
    recommendations() {
      return this.homeRecommendations || [];
    },
    /** Sempre que houver sorteio; favoritos e o último aberto ficam de fora dele. */
    showRecommended() {
      return this.recommendations.length > 0;
    }
  },
  methods: {
    ...mapActions("preferences", [
      "openGlobalSearches",
      "openFavorites",
      "getFavorites",
      "getHomeRecommendations",
      "openContent"
    ]),
    ...mapActions("general", [
      "initializeApp",
      "logEvent",
      "routePathRedirect"
    ]),
    ...mapMutations("general", ["setSectionsPanel"]),
    ...mapActions("drugs", ["clearNavigationHistory"]),
    ...mapActions("purchases", ["openPurchases"]),

    onSearch() {
      this.logEvent("home_search_click");
      this.openGlobalSearches();
    },
    onAssistant() {
      this.routePathRedirect({ path: "/ai-conversations" });
    },
    onPrescriptions() {
      this.routePathRedirect({ path: "/prescriptions" });
    },
    onContinue(item) {
      this.logEvent({
        name: "home_continue_click",
        object: { kind: contentRoutes.getContentKind(item), id: item.id }
      });
      this.openContent(item);
    },
    onFavoriteSelect({ item, position }) {
      this.logEvent({
        name: "home_favorite_click",
        object: {
          kind: contentRoutes.getContentKind(item),
          id: item.id,
          position
        }
      });
      this.openContent(item);
    },
    onFavoritesViewAll() {
      this.logEvent("home_favorites_view_all");
      this.openFavorites();
    },
    onRecommendedSelect({ item, position }) {
      this.logEvent({
        name: "home_recommended_click",
        object: {
          kind: contentRoutes.getContentKind(item),
          id: item.id,
          position
        }
      });
      this.openContent(item);
    },
    onRecommendedShuffle() {
      if (this.shuffling) return;
      this.shuffling = true;
      setTimeout(() => {
        this.shuffling = false;
      }, SHUFFLE_DEBOUNCE);
      this.logEvent("home_recommended_shuffle");
      this.getHomeRecommendations().then(() => this.logRecommendedImpression());
    },
    onQuickAccessSelect(section) {
      this.logEvent({
        name: "home_quick_access_click",
        object: { id: section.id || section.icon }
      });
      sectionsEnum.goToSection({ section, dispatch: this.$store.dispatch });
    },
    /** O painel "Todas as seções" vive no App, junto da barra inferior. */
    onOpenAllSections() {
      this.logEvent("home_all_sections_open");
      this.setSectionsPanel(true);
    },
    onUpsellSelect() {
      this.logEvent("home_upsell_click");
      this.openPurchases();
    },

    logRecommendedImpression() {
      if (!this.showRecommended) return;
      this.logEvent({
        name: "home_recommended_impression",
        object: { ids: this.recommendations.map(i => i.id).join(",") }
      });
    },
    logUpsellImpression() {
      if (this.isPremium) return;
      try {
        if (window.sessionStorage.getItem(UPSELL_IMPRESSION_KEY)) return;
        window.sessionStorage.setItem(UPSELL_IMPRESSION_KEY, "1");
      } catch {
        // sessionStorage indisponível: registra a impressão mesmo assim.
      }
      this.logEvent("home_upsell_impression");
    },
    logHomeView() {
      this.logEvent({
        name: "home_view",
        object: {
          is_premium: !!this.isPremium,
          has_history: this.hasHistory,
          has_favorites: this.hasFavorites,
          favorites_count: this.favoriteItems.length,
          recommended_shown: this.showRecommended
        }
      });
    }
  },
  watch: {
    /** Revalida os favoritos depois que o usuário altera a lista. */
    triggerFavoritesChange(value) {
      if (!value) return;
      this.getFavorites({ silent: true, force: true });
    }
  },
  data() {
    return {
      mdiCreation,
      mdiPrescription,
      shuffling: false
    };
  },
  mounted() {
    this._setupScreenContent(this);
  },
  created: async function() {
    await this.initializeApp();
    this.clearNavigationHistory();

    // Recomendados: sorteio local, sem rede.
    this.getHomeRecommendations().then(() => this.logRecommendedImpression());

    // Favoritos: única requisição HTTP da Home, em background e não aguardada.
    this.getFavorites({ silent: true })
      .catch(() => {})
      .then(() => {
        this.logHomeView();
        this.logUpsellImpression();
      });

    if (this.$route.query.category) {
      await sectionsEnum.goToSection({
        id: this.$route.query.category,
        dispatch: this.$store.dispatch
      });
    }
  }
};
</script>
